import { useState, useEffect } from "react";
import { useAppSelector } from "../../hooks/redux";
import { useModalManager } from "../../hooks/useModalManager";
import { useAppUpdate } from "../../hooks/useAppUpdate";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import ModalRenderer from "../../components/modal/ModalRenderer";
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import Button from "../../components/ui/Button";
import DataTable, { DataTableColumn } from "../../components/ui/DataTable";
import RowActionsMenu, { RowMenuItem } from "../../components/ui/RowActionsMenu";
import Pagination from "../../components/common/Pagination";
import { badgeBaseClass, badgeTones } from "../../components/ui/StatusBadge";
import { cn } from "../../DashboardPage/lib/utils";

const PAGE_SIZE = 10;

/**
 * App updates have their own two-value vocabulary, unrelated to pitch statuses,
 * so they reuse StatusBadge's shell and tone tokens rather than its map.
 *
 * "Published" is a real success/live state. "Draft" was amber, but a draft is
 * not a warning — it is simply not live yet — so it takes the neutral tone,
 * matching how `draft` already reads on the pitch badge.
 */
const UPDATE_STATUS_TONES: Record<string, string> = {
  published: badgeTones.success,
  draft: badgeTones.neutral,
};

export default function AppUpdates() {
  const { user } = useAppSelector((state) => state.auth);
  const { openModal, closeModal, isOpen } = useModalManager();

  // Use the custom hook for app updates
  const {
    appUpdates,
    loading: appUpdateLoading,
    pagination: appUpdatesPagination,
    fetchAppUpdates,
    createDraft,
    publishUpdate,
    updateAppUpdate,
    deleteAppUpdate: deleteAppUpdateFromHook,
  } = useAppUpdate();

  // App Update modal state
  const [editUpdateId, setEditUpdateId] = useState<number | undefined>();
  const [editUpdateContent, setEditUpdateContent] = useState("");
  const [editUpdateTitle, setEditUpdateTitle] = useState("");
  const [editUpdateStatus, setEditUpdateStatus] = useState<"published" | "draft">("draft");
  const [updateMode, setUpdateMode] = useState<"create" | "edit">("create");
  const [deleteUpdateId, setDeleteUpdateId] = useState<number | undefined>();

  useEffect(() => {
    if (user?.role === "admin") {
      fetchAppUpdates({ page: 1, limit: PAGE_SIZE });
    }
  }, [user?.role, fetchAppUpdates]);

  // Create Update modal handlers
  const handleOpenCreateUpdateModal = () => {
    setUpdateMode("create");
    setEditUpdateContent("");
    setEditUpdateTitle("");
    setEditUpdateStatus("draft");
    setEditUpdateId(undefined);
    openModal("CREATE_UPDATE");
  };

  const handleCloseCreateUpdateModal = () => {
    closeModal("CREATE_UPDATE");
    setEditUpdateContent("");
    setEditUpdateTitle("");
    setEditUpdateStatus("draft");
    setEditUpdateId(undefined);
    setUpdateMode("create");
  };

  const handleDraftUpdate = async (title: string, content: string) => {
    try {
      if (updateMode === "edit" && editUpdateId) {
        await updateAppUpdate(editUpdateId, title, content, "draft");
      } else {
        await createDraft(title, content);
      }
      handleCloseCreateUpdateModal();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handlePublishUpdate = async (title: string, content: string) => {
    try {
      if (updateMode === "edit" && editUpdateId) {
        await updateAppUpdate(editUpdateId, title, content, "published");
      } else {
        await publishUpdate(title, content);
      }
      handleCloseCreateUpdateModal();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  // Edit Update handler
  const handleEditUpdate = (updateId: number, title: string, content: string, status: "published" | "draft") => {
    setUpdateMode("edit");
    setEditUpdateId(updateId);
    setEditUpdateTitle(title);
    setEditUpdateContent(content);
    setEditUpdateStatus(status);
    openModal("CREATE_UPDATE");
  };

  // Delete Update handlers
  const handleOpenDeleteUpdateModal = (updateId: number) => {
    setDeleteUpdateId(updateId);
    openModal("DELETE_UPDATE_CONFIRM");
  };

  const handleCloseDeleteUpdateModal = () => {
    closeModal("DELETE_UPDATE_CONFIRM");
    setDeleteUpdateId(undefined);
  };

  const handleDeleteUpdateConfirmed = async () => {
    if (!deleteUpdateId) return;

    try {
      await deleteAppUpdateFromHook(deleteUpdateId);
      handleCloseDeleteUpdateModal();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  // App Updates Pagination handler
  const handleAppUpdatesPageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= appUpdatesPagination.totalPages) {
      fetchAppUpdates({ page: newPage, limit: PAGE_SIZE });
    }
  };

  const columns: DataTableColumn<any>[] = [
    {
      key: "id",
      header: "ID",
      cell: (update) => <span className="tabular-nums text-neutral-600">{update.id}</span>,
    },
    {
      key: "title",
      header: "Title",
      cell: (update) => (
        <span className="font-medium">{update.title || `App update ${update.id}`}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (update) => (
        <span
          className={cn(
            badgeBaseClass,
            UPDATE_STATUS_TONES[update.status] ?? badgeTones.neutral,
            "capitalize"
          )}
        >
          {update.status}
        </span>
      ),
    },
    {
      key: "views",
      header: "Views",
      align: "center",
      cell: (update) => <span className="tabular-nums text-neutral-600">{update.views}</span>,
    },
    {
      key: "publishDate",
      header: "Publish Date",
      cell: (update) =>
        update.status === "published" ? (
          <span className="text-neutral-600">
            {new Date(update.updatedAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        ) : (
          <span className="text-neutral-400">Not Published</span>
        ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (update) => (
        <RowActionsMenu label={`Actions for ${update.title || `app update ${update.id}`}`}>
          <RowMenuItem
            icon={<PencilIcon className="w-4 h-4" />}
            onClick={() =>
              handleEditUpdate(update.id, update.title || "", update.updateData, update.status)
            }
          >
            Edit
          </RowMenuItem>
          <RowMenuItem
            tone="danger"
            icon={<TrashIcon className="w-4 h-4" />}
            onClick={() => handleOpenDeleteUpdateModal(update.id)}
          >
            Delete
          </RowMenuItem>
        </RowActionsMenu>
      ),
    },
  ];

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "App Updates" }]}
        className="mb-6"
        title="App Updates"
        description="Release notes shown to users in What's New."
        actions={
          user?.role === "admin" ? (
            <Button onClick={handleOpenCreateUpdateModal} className="w-full sm:w-auto">
              Create New Update
            </Button>
          ) : undefined
        }
      />

      <DataTable
        columns={columns}
        rows={appUpdates}
        getRowKey={(update) => update.id}
        loading={appUpdateLoading}
        skeletonRows={PAGE_SIZE}
        emptyMessage={
          <span className="block">
            <span className="block text-sm font-medium text-black">No app updates found</span>
            <span className="block mt-1 text-sm text-neutral-500">
              Create an update to publish release notes to users.
            </span>
          </span>
        }
      />

      {/* Shared Pagination, with the totals so it can show the range line. */}
      {appUpdatesPagination.totalItems > 0 && (
        <div className="mt-4">
          <Pagination
            page={appUpdatesPagination.currentPage}
            totalPages={appUpdatesPagination.totalPages}
            setPage={handleAppUpdatesPageChange}
            totalItems={appUpdatesPagination.totalItems}
            limit={appUpdatesPagination.itemsPerPage}
          />
        </div>
      )}

      {/* Create Update Modal */}
      {isOpen("CREATE_UPDATE") && (
        <ModalRenderer
          type="CREATE_UPDATE"
          isOpen={isOpen("CREATE_UPDATE")}
          onClose={handleCloseCreateUpdateModal}
          onDraft={handleDraftUpdate}
          onPublish={handlePublishUpdate}
          loading={appUpdateLoading}
          mode={updateMode}
          initialContent={editUpdateContent}
          initialTitle={editUpdateTitle}
          updateId={editUpdateId}
          currentStatus={editUpdateStatus}
        />
      )}

      {/* Delete Update Confirmation Modal */}
      {isOpen("DELETE_UPDATE_CONFIRM") && (
        <ModalRenderer
          type="CONFIRMATION"
          isOpen={isOpen("DELETE_UPDATE_CONFIRM")}
          onClose={handleCloseDeleteUpdateModal}
          onConfirm={handleDeleteUpdateConfirmed}
          title="Delete Update"
          message="Are you sure you want to delete this update? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          isDestructive={true}
        />
      )}
    </div>
  );
}
